import React, { useMemo, useState, useCallback, useEffect } from 'react';
import { ChevronLeft, ChevronRight, Phone, Calendar, User, Lock } from 'lucide-react';

import { useLanguage } from '../../contexts/LanguageContext';
import { formatCurrency, safeAmount, formatDate } from '../../utils/helpers';
import { Skeleton } from '../ui';

// Stages that are terminal — no drag allowed (Lost removed so accidentally moved prequalifications can be moved out)
const LOCKED_STAGES = ['Won', 'Awarded'];
const isLocked = (stage) => LOCKED_STAGES.includes(stage);

// ─── Stage color config ────────────────────────────────────────────────────────
const STAGE_STYLE = {
  // Prequalification
  'New':                  { dot: '#3b82f6', badge: '#3b82f6', col: '#eff6ff', border: '#bfdbfe' }, // blue
  'Submitted':            { dot: '#06b6d4', badge: '#06b6d4', col: '#ecfeff', border: '#a5f3fc' }, // cyan
  'Pending':              { dot: '#f59e0b', badge: '#f59e0b', col: '#fffbeb', border: '#fde68a' }, // amber
  'Enlisted':             { dot: '#10b981', badge: '#10b981', col: '#f0fdf4', border: '#a7f3d0' }, // green
  'Disqualified':         { dot: '#ef4444', badge: '#ef4444', col: '#fef2f2', border: '#fecaca' }, // red

  // Proposal/Tender
  'In Process':           { dot: '#a855f7', badge: '#a855f7', col: '#faf5ff', border: '#e9d5ff' }, // purple
  'Won':                  { dot: '#10b981', badge: '#10b981', col: '#f0fdf4', border: '#a7f3d0' }, // emerald
  'Awarded':              { dot: '#10b981', badge: '#10b981', col: '#f0fdf4', border: '#a7f3d0' }, // emerald
  'Lost':                 { dot: '#ef4444', badge: '#ef4444', col: '#fff1f2', border: '#fecdd3' }, // rose
  'Hold':                 { dot: '#6b7280', badge: '#6b7280', col: '#f9fafb', border: '#e5e7eb' }, // gray
};

// ─── Prequalification Card ────────────────────────────────────────────────────────────────
function LeadCard({ prequalification, onSelectLead, isNew, isViewed, accent, onDragStart, onDragEnd, isDragging, dragBlocked, isPendingConfirm }) {
  const { t } = useLanguage();
  const displayName = prequalification.clientName?.includes('null')
    ? prequalification.clientName.replace(/\s*null\s*/, '').trim()
    : prequalification.clientName;

  const locked = isLocked(prequalification.stage);
  const isDragDisabled = locked || dragBlocked;

  const docStatus = prequalification.documentStatus;
  let docColorClass = 'text-gray-400';
  if (docStatus) {
    if (docStatus.includes('Received') || docStatus === 'Complete' || docStatus === 'Submitted') docColorClass = 'text-green-600';
    else if (docStatus.includes('To be') || docStatus === 'Pending' || docStatus === 'In Process') docColorClass = 'text-amber-600';
    else if (docStatus.includes('Missing')) docColorClass = 'text-red-600';
  }

  return (
    <div
      draggable={!isDragDisabled}
      onDragStart={isDragDisabled ? undefined : (e) => onDragStart(e, prequalification)}
      onDragEnd={isDragDisabled ? undefined : onDragEnd}
      onClick={() => onSelectLead(prequalification)}
      className={`
        bg-white rounded-lg border select-none
        transition-all duration-150 overflow-hidden
        ${isDragDisabled
          ? 'cursor-pointer opacity-90'
          : 'cursor-grab active:cursor-grabbing hover:shadow-md hover:-translate-y-0.5'
        }
        ${isDragging ? 'opacity-40 scale-95 shadow-lg ring-2 ring-blue-300' : ''}
        ${isPendingConfirm ? 'ring-2 ring-amber-400 ring-offset-1' : ''}
        ${isNew ? 'border-l-[3px]' : 'border-gray-200'}
        ${isViewed ? 'opacity-55' : ''}
      `}
      style={isNew ? { borderLeftColor: '#f59e0b', borderTopColor: '#fde68a', borderRightColor: '#fde68a', borderBottomColor: '#fde68a' } : {}}
    >
      <div className="h-[2px]" style={{ background: `linear-gradient(90deg,${accent}88,transparent)` }} />

      <div className="px-3 py-2.5 space-y-2.5">
        <div className="flex items-start justify-between gap-1">
          <span className="font-semibold text-gray-900 text-[13px] leading-snug line-clamp-2">{displayName || 'Unnamed Client'}</span>
          <div className="flex items-center gap-1 flex-shrink-0">
            {locked && (
              <span
                className="flex items-center gap-0.5 text-[9px] font-bold px-1.5 py-0.5 rounded"
                style={{
                  background: prequalification.stage === 'Won' ? '#d1fae5' : '#fee2e2',
                  color: prequalification.stage === 'Won' ? '#065f46' : '#991b1b',
                }}
                title={t('leadLockedTitle') || `This prequalification is ${t('stage' + prequalification.stage.replace(/\s+/g, '')) || prequalification.stage} and cannot be moved`}
              >
                <Lock className="w-2.5 h-2.5" />
                {t('stage' + prequalification.stage.replace(/\s+/g, '')) || prequalification.stage}
              </span>
            )}
            {isPendingConfirm && (
              <span className="flex items-center gap-0.5 text-[9px] font-bold px-1.5 py-0.5 rounded bg-amber-100 text-amber-700 animate-pulse">
                ⏳ {t('confirming') || 'Confirming...'}
              </span>
            )}
            {!locked && !isPendingConfirm && isNew && (
              <span className="text-[9px] font-bold px-1.5 py-0.5 rounded bg-yellow-100 text-yellow-700 uppercase">{t('new') || 'New'}</span>
            )}
          </div>
        </div>

        <div className="flex flex-wrap items-center gap-x-2 gap-y-1 mt-1">
          {prequalification.phone && (
            <div className="flex items-center gap-1 text-[11px] text-gray-500 font-mono">
              <Phone className="w-2.5 h-2.5" style={{ color: accent }} /> {prequalification.phone}
            </div>
          )}
          {prequalification.source && (
            <div className="flex items-center gap-1 text-[11px] text-gray-500 bg-gray-50 px-1.5 py-0.5 rounded border border-gray-100">
              {prequalification.source?.name || prequalification.source}
            </div>
          )}
          {prequalification.city && (
            <div className="flex items-center gap-1 text-[11px] text-gray-500">
              📍 {prequalification.city}
            </div>
          )}
        </div>

        <div className="bg-slate-50 border border-slate-100 rounded p-2 grid grid-cols-2 gap-2">
          <div className="flex flex-col">
            <span className="text-[9px] uppercase tracking-wider text-gray-400 font-bold mb-0.5">Category</span>
            <span className="text-[11px] font-semibold text-gray-700 truncate" title={prequalification.services?.length > 0 ? prequalification.services.map(s => typeof s === 'string' ? s : s.name).join(', ') : ''}>
              {prequalification.services?.length > 0 ? prequalification.services.map(s => typeof s === 'string' ? s : s.name).join(', ') : <span className="text-gray-400 italic">N/A</span>}
            </span>
          </div>
          <div className="flex flex-col border-l border-slate-200 pl-2">
            <span className="text-[9px] uppercase tracking-wider text-gray-400 font-bold mb-0.5">{t('documentStatus') || 'Doc Status'}</span>
            <span className={`text-[11px] font-semibold truncate ${docColorClass}`}>
              {docStatus || <span className="italic font-normal text-gray-400">Not Set</span>}
            </span>
          </div>
        </div>

        <div className="flex items-center justify-between mt-1 pt-1 border-t border-gray-100">
          {prequalification.manager ? (
            <div className="flex items-center gap-1.5">
              <User className="w-3 h-3 text-gray-400" />
              <span className="text-[10px] text-gray-500 truncate max-w-[80px]">{prequalification.manager}</span>
            </div>
          ) : <div/>}

          <div className="flex items-center gap-2">
            {prequalification.followUpDate && (
              <span className="flex items-center gap-1 text-[10px] text-amber-600 font-medium bg-amber-50 px-1.5 py-0.5 rounded" title="Follow Up">
                <Calendar className="w-2.5 h-2.5" />
                {formatDate(prequalification.followUpDate)}
              </span>
            )}
            {!prequalification.followUpDate && (
              <span className="flex items-center gap-1 text-[10px] text-gray-400">
                <Calendar className="w-3 h-3" />
                {formatDate(prequalification.submissionDate || prequalification.inquiryDate || prequalification.createdAt)}
              </span>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

// ─── Kanban Column ─────────────────────────────────────────────────────────────
function KanbanColumn({
  stage, prequalifications, onSelectLead, newPrequalificationIds, viewedPrequalificationIds,
  collapsed, onToggle,
  draggingLead, onDragStart, onDragEnd, onDrop,
  dragBlocked, pendingWonPrequalificationId,
}) {
  const { t } = useLanguage();
  const style = STAGE_STYLE[stage] || STAGE_STYLE['New'];
  const [isOver, setIsOver] = useState(false);
  const [visibleCount, setVisibleCount] = useState(30);
  const total = prequalifications.reduce((s, l) => s + safeAmount(l.quotationAmount), 0);

  const handleDragOver = useCallback((e) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    setIsOver(true);
  }, []);

  const handleDragLeave = useCallback((e) => {
    // Only hide if leaving the column entirely
    if (!e.currentTarget.contains(e.relatedTarget)) {
      setIsOver(false);
    }
  }, []);

  const handleDrop = useCallback((e) => {
    e.preventDefault();
    setIsOver(false);
    onDrop(stage);
  }, [stage, onDrop]);

  // Can drop only if dragging card is NOT already in this column
  const canDrop = draggingLead && draggingLead.stage !== stage;

  if (collapsed) {
    return (
      <div
        className="flex flex-col items-center bg-white border border-gray-200 rounded-xl shadow-sm cursor-pointer hover:shadow-md transition-all duration-150 py-4 gap-3"
        style={{ width: 36, minWidth: 36, flexShrink: 0 }}
        onClick={onToggle}
        title={`${t('expand') || 'Expand'} ${t('stage' + stage.replace(/\s+/g, '')) || stage}`}
      >
        <div className="w-2 h-2 rounded-full flex-shrink-0" style={{ background: style.dot }} />
        <span
          className="text-[10px] font-semibold text-gray-600 whitespace-nowrap"
          style={{ writingMode: 'vertical-rl', textOrientation: 'mixed', transform: 'rotate(180deg)' }}
        >
          {t('stage' + stage.replace(/\s+/g, '')) || stage}
        </span>
        <span
          className="text-[9px] font-bold text-white rounded-full px-1 py-0.5 leading-none"
          style={{ background: style.badge }}
        >
          {prequalifications.length}
        </span>
        <ChevronRight className="w-3 h-3 text-gray-400 mt-auto" />
      </div>
    );
  }

  return (
    <div className="flex flex-col flex-1 min-w-0">
      {/* Column header */}
      <div className="flex items-center justify-between bg-white border border-b-0 border-gray-200 rounded-t-xl px-2 py-2 gap-1">
        <div className="flex items-center gap-1.5 min-w-0">
          <div className="w-2 h-2 rounded-full flex-shrink-0" style={{ background: style.dot }} />
          <span className="text-[11px] font-semibold text-gray-800 truncate">{t('stage' + stage.replace(/\s+/g, '')) || stage}</span>
          <span
            className="text-[10px] font-bold text-white rounded-full px-1.5 py-0.5 leading-none flex-shrink-0"
            style={{ background: style.badge }}
          >
            {prequalifications.length}
          </span>
        </div>
        <button
          onClick={onToggle}
          className="flex-shrink-0 p-0.5 rounded hover:bg-gray-100 transition-colors"
          title={t('collapseColumn') || "Collapse column"}
        >
          <ChevronLeft className="w-3 h-3 text-gray-400" />
        </button>
      </div>

      {/* Drop Zone */}
      <div
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
        className="flex-1 border border-gray-200 rounded-b-xl p-1.5 space-y-1.5 overflow-y-auto transition-all duration-150"
        style={{
          background: canDrop && isOver ? style.dot + '18' : style.col,
          borderColor: canDrop && isOver ? style.dot : style.border,
          minHeight: 80,
          maxHeight: 'calc(100vh - 280px)',
          boxShadow: canDrop && isOver ? `inset 0 0 0 2px ${style.dot}55` : 'none',
        }}
      >
        {/* Drop indicator */}
        {canDrop && isOver && (
          <div
            className="flex items-center justify-center py-1.5 mb-1 rounded-lg border-2 border-dashed text-[10px] font-semibold"
            style={{ borderColor: style.dot, color: style.dot, background: style.dot + '0D' }}
          >
            {t('dropTo') || 'Drop →'} {t('stage' + stage.replace(/\s+/g, '')) || stage}
          </div>
        )}

        {prequalifications.length === 0 && !isOver ? (
          <div className="flex items-center justify-center py-4">
            <span className="text-[10px] text-gray-400 select-none">{t('noCards') || 'No cards'}</span>
          </div>
        ) : (
          <>
            {prequalifications.slice(0, visibleCount).map((prequalification) => (
              <LeadCard
                key={prequalification.id}
                prequalification={prequalification}
                onSelectLead={onSelectLead}
                isNew={newPrequalificationIds.includes(prequalification.id)}
                isViewed={viewedPrequalificationIds.includes(prequalification.id)}
                accent={style.dot}
                onDragStart={onDragStart}
                onDragEnd={onDragEnd}
                isDragging={draggingLead?.id === prequalification.id}
                dragBlocked={dragBlocked}
                isPendingConfirm={prequalification.id === pendingWonPrequalificationId}
              />
            ))}
            {prequalifications.length > visibleCount && (
              <button
                type="button"
                onClick={() => setVisibleCount((prev) => prev + 30)}
                className="w-full py-2 mt-1 text-xs font-semibold text-blue-600 bg-white border border-blue-200 hover:bg-blue-50 rounded-lg transition-colors shadow-sm"
              >
                {t('loadMore') || 'Load More'} ({prequalifications.length - visibleCount})
              </button>
            )}
          </>
        )}

        {/* Column total */}
        {total > 0 && (
          <div className="pt-1 pb-0.5 text-center">
            <span className="text-[9px] font-semibold text-gray-400">{formatCurrency(total)}</span>
          </div>
        )}
      </div>
    </div>
  );
}

// ─── Main Board ────────────────────────────────────────────────────────────────
export default function PrequalificationsKanbanView({
  filteredData,
  isLoading,
  onSelectLead,
  onStageChange,
  newPrequalificationIds = [],
  viewedPrequalificationIds = [],
  stages = [], // Replace STAGES import with dynamic prop
}) {
  const [collapsed, setCollapsed] = useState({});
  const [draggingLead, setDraggingLead] = useState(null);
  // Optimistic local stage overrides: { [prequalificationId]: targetStage }
  // Card moves instantly on drop — no double-jump waiting for API refetch
  const [stageOverrides, setStageOverrides] = useState({});
  // Tracks the prequalification ID awaiting Won booking confirmation.
  // While set: ALL cards are drag-disabled to prevent double-drag race conditions.
  // Cleared when: booking confirmed, booking cancelled, or data refreshes with Won stage.
  const [pendingWonPrequalificationId, setPendingWonPrequalificationId] = useState(null);

  const visibleStages = stages;

  const toggleCol = (stage) =>
    setCollapsed((prev) => ({ ...prev, [stage]: !prev[stage] }));

  // Group prequalifications by stage — apply local optimistic overrides first
  const grouped = useMemo(() => {
    const map = {};
    stages.forEach((s) => { map[s] = []; });
    filteredData.forEach((prequalification) => {
      // Use override stage if set (optimistic), else actual stage
      let s = stageOverrides[prequalification.id] || prequalification.stage || stages[0] || 'New';
      if (map[s]) map[s].push({ ...prequalification, stage: s });
      // If a prequalification has a stage not in the current pipeline, we could put it in a fallback column or ignore.
      // Ignoring it is better so it doesn't crash or create random columns.
    });
    return map;
  }, [filteredData, stageOverrides, stages]);

  // Clear stageOverride when real data catches up (API refresh completed)
  // Also clear pendingWonPrequalificationId when the prequalification is confirmed Won in DB
  useEffect(() => {
    setStageOverrides((prev) => {
      const next = { ...prev };
      let changed = false;
      filteredData.forEach((prequalification) => {
        if (next[prequalification.id] && next[prequalification.id] === prequalification.stage) {
          delete next[prequalification.id];
          changed = true;
        }
      });
      return changed ? next : prev;
    });
    // Clear pending Won lock if the prequalification is now Won in real data (confirmed)
    if (pendingWonPrequalificationId) {
      const confirmedLead = filteredData.find(
        (l) => l.id === pendingWonPrequalificationId && l.stage === 'Won'
      );
      if (confirmedLead) setPendingWonPrequalificationId(null);
    }
  }, [filteredData, pendingWonPrequalificationId]);

  // ── Drag handlers ────────────────────────────────────────────────────────────
  const handleDragStart = useCallback((e, prequalification) => {
    setDraggingLead(prequalification);
    e.dataTransfer.effectAllowed = 'move';
    // Set ghost text
    e.dataTransfer.setData('text/plain', prequalification.id);
  }, []);

  const handleDragEnd = useCallback(() => {
    setDraggingLead(null);
  }, []);

  const handleDrop = useCallback(async (targetStage) => {
    if (!draggingLead) return;
    if (draggingLead.stage === targetStage) {
      setDraggingLead(null);
      return;
    }
    // Block if source prequalification is locked
    if (isLocked(draggingLead.stage)) {
      setDraggingLead(null);
      return;
    }

    const prequalification = draggingLead;
    const oldStage = prequalification.stage;
    setDraggingLead(null);

    // ── Won & Site Visited stage special handling ────────────────────────────────────────────
    // When dropping onto Won or Site Visited, open the modal with the ORIGINAL stage so that
    // PrequalificationDetailModal can fire and show the Confirm Booking modal (which handles saving + BMS API submission itself).
    // We do NOT fire onStageChange here — the booking confirm flow does it.
    if (targetStage === 'Won' || targetStage === 'Site Visited') {
      // Revert the optimistic override — modal will set it after booking confirm
      setStageOverrides((prev) => {
        const next = { ...prev };
        delete next[prequalification.id];
        return next;
      });
      // Lock ALL cards from dragging until booking is confirmed or cancelled.
      setPendingWonPrequalificationId(prequalification.id);
      setTimeout(() => setPendingWonPrequalificationId(null), 1000);

      if (onSelectLead) {
        // Open modal with ORIGINAL stage + pending flags
        onSelectLead({ 
          ...prequalification, 
          stage: oldStage, 
          _pendingWon: targetStage === 'Won',
          _pendingSiteVisited: targetStage === 'Site Visited'
        });
      }
      return;
    }

    // ── Generic stage change drag handling ──
    // Revert any optimistic override
    setStageOverrides((prev) => {
      const next = { ...prev };
      delete next[prequalification.id];
      return next;
    });
    
    // Lock drag temporarily
    setPendingWonPrequalificationId(prequalification.id);
    setTimeout(() => setPendingWonPrequalificationId(null), 1000);

    if (onSelectLead) {
      // Open modal with ORIGINAL stage + pending flag
      onSelectLead({ 
        ...prequalification, 
        stage: oldStage, 
        _pendingStageChange: targetStage,
        pendingTransition: { source: 'kanban' }
      });
    }
  }, [draggingLead, onStageChange, onSelectLead]);

  // Loading skeleton
  if (isLoading && filteredData.length === 0) {
    return (
      <div className="flex gap-1.5 w-full">
        {visibleStages.map((stage) => {
          const style = STAGE_STYLE[stage];
          return (
            <div key={stage} className="flex-1 min-w-0">
              <div className="bg-white border border-b-0 border-gray-200 rounded-t-xl px-2 py-2 flex items-center gap-1.5">
                <div className="w-2 h-2 rounded-full" style={{ background: style.dot }} />
                <Skeleton width={60} height={12} animation="wave" />
              </div>
              <div className="border border-gray-200 rounded-b-xl p-1.5 space-y-1.5" style={{ background: style.col, minHeight: 100 }}>
                {[1, 2].map((i) => (
                  <div key={i} className="bg-white rounded-lg border border-gray-200 p-2 space-y-1.5">
                    <Skeleton width="80%" height={12} animation="wave" />
                    <Skeleton width="60%" height={10} animation="wave" />
                  </div>
                ))}
              </div>
            </div>
          );
        })}
      </div>
    );
  }

  return (
    <div
      className="flex gap-1.5 w-full"
      style={{ userSelect: draggingLead ? 'none' : 'auto', alignItems: 'stretch' }}
    >
      {visibleStages.map((stage) => (
        <KanbanColumn
          key={stage}
          stage={stage}
          prequalifications={grouped[stage] || []}
          onSelectLead={(prequalification) => {
            // Any card click while a Won confirmation is pending clears the lock.
            // Cases:
            //   - Same prequalification re-clicked (user cancelled booking, now re-opening normally)
            //   - Different prequalification clicked (user abandoned the Won flow entirely)
            // In both cases releasing the drag lock is the correct behaviour.
            if (pendingWonPrequalificationId) setPendingWonPrequalificationId(null);
            onSelectLead(prequalification);
          }}
          newPrequalificationIds={newPrequalificationIds}
          viewedPrequalificationIds={viewedPrequalificationIds}
          collapsed={!!collapsed[stage]}
          onToggle={() => toggleCol(stage)}
          draggingLead={draggingLead}
          onDragStart={handleDragStart}
          onDragEnd={handleDragEnd}
          onDrop={handleDrop}
          dragBlocked={!!pendingWonPrequalificationId}
          pendingWonPrequalificationId={pendingWonPrequalificationId}
        />
      ))}
    </div>
  );
}
